import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { cardHref } from '@/lists/cellValue'
import type { ListRow } from '@/lists/types'
import { AttachmentsPanel } from './AttachmentsPanel'
import { daysUntil } from './DealRecordView'
import { activateContract, fetchContract, prolongContract, terminateContract } from './documentApi'
import { HistoryPanel } from './HistoryPanel'
import { Facts, Panel, RecordHeader, RecordTabs, StageBar, StateView, type Fact, type HeaderAction, type MenuAction, type StageStep } from './record'
import { listPath } from './recordHelpers'
import {
  DateField,
  FailureAlert,
  FormModal,
  RecordFailure,
  RecordSkeleton,
  RequiredText,
  StatusBadge,
  copyLinkItem,
  failureText,
  labelOf,
  numberOf,
  textOf,
  todayIso,
} from './recordKit'
import type { RecordViewProps } from './types'

/**
 * Карточка договора (Contract.dc.html, тикет #73).
 *
 * Основное действие по состоянию: черновик вводят в действие (дата подписания
 * обязательна), срочный действующий — продлевают. Продление двигает срок **только вперёд**
 * — проверяет это домен, и отказ показывается его словами внутри окна: своя проверка на
 * клиенте завела бы второе место, где решается, что считать продлением. Бессрочный договор
 * продлевать нечего — «продление» бессрочного на год **ограничило** бы его, кнопки нет.
 * Расторжение — в «⋯» за чертой, с датой и обязательной причиной.
 *
 * Пригодность «можно ли сегодня оформить заказ» — с сервера (`usableToday`), а не по датам
 * на клиенте: статус и даты — разные утверждения, и решает их одна функция домена.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - **Кредитный лимит, оборот, пени, просрочка по договору** — у договора их нет (лимит —
 *   у контрагента, на его вкладке «Финансы»).
 * - **Доп. соглашения, подписанты и доверенности, «Отправить на согласование»** — ни
 *   сущностей, ни эндпоинтов.
 * - **Причина расторжения на карточке** — колонки в `crm.contract` нет, причина уходит в
 *   журнал приложения.
 * - **Акты сверки** в документах по договору — отдельной сущности нет.
 */
export function ContractRecordView({ recordId, record, context, notices }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('documents')
  const [failure, setFailure] = useState<string | null>(null)
  const [dialog, setDialog] = useState<'activate' | 'prolong' | 'terminate' | null>(null)
  const [dialogFailure, setDialogFailure] = useState<string | null>(null)
  const [signedOn, setSignedOn] = useState(todayIso())
  const [validUntil, setValidUntil] = useState('')
  const [prolongTo, setProlongTo] = useState('')
  const [terminatedOn, setTerminatedOn] = useState(todayIso())
  const [reason, setReason] = useState('')

  const contract = useQuery({ queryKey: ['contract', recordId], queryFn: ({ signal }) => fetchContract(recordId, signal), retry: false })
  const mayReadInvoices = can(user, 'billing.invoice', 'read')
  const mayReadDocuments = can(user, 'billing.document', 'read')
  const invoices = useQuery({
    queryKey: ['contract-invoices', recordId],
    enabled: mayReadInvoices,
    queryFn: ({ signal }) =>
      fetchPage(
        'billing.invoice',
        {
          columns: ['number', 'issued_on', 'due_on', 'total', 'paid_total', 'currency', 'status'],
          filters: [{ column: 'contract_id', operator: 'eq', value: recordId }],
          sort: [{ column: 'issued_on', descending: true }],
          pageSize: 50,
        },
        null,
        signal,
      ),
    retry: false,
  })
  const documents = useQuery({
    queryKey: ['contract-documents', recordId],
    enabled: mayReadDocuments,
    queryFn: ({ signal }) =>
      fetchPage(
        'billing.document',
        {
          columns: ['number', 'document_kind', 'document_date', 'total', 'currency', 'status'],
          filters: [{ column: 'contract_id', operator: 'eq', value: recordId }],
          sort: [{ column: 'document_date', descending: true }],
          pageSize: 50,
        },
        null,
        signal,
      ),
    retry: false,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contract', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'billing.contract', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'billing.contract'] })
    void queryClient.invalidateQueries({ queryKey: ['contracts'] })
  }
  const closeDialog = () => {
    setDialog(null)
    setDialogFailure(null)
  }
  const mutationOptions = {
    onSuccess: () => {
      setFailure(null)
      setDialog(null)
      setDialogFailure(null)
      setReason('')
      refresh()
    },
    // The domain's refusal ("only forward", "reason required") is read where the user is
    // typing, not under a closed dialog.
    onError: (error: unknown) => setDialogFailure(failureText(error, t('app.error'))),
  }
  const activate = useMutation({ mutationFn: () => activateContract(recordId, signedOn, signedOn, validUntil || null), ...mutationOptions })
  const prolong = useMutation({ mutationFn: () => prolongContract(recordId, prolongTo), ...mutationOptions })
  const terminate = useMutation({ mutationFn: () => terminateContract(recordId, terminatedOn, reason.trim()), ...mutationOptions })

  if (contract.isLoading) return <RecordSkeleton />
  if (!contract.data) {
    return <RecordFailure error={contract.error} onRetry={() => void contract.refetch()} listPath={listPath('billing.contract', '/contracts')} />
  }
  const current = contract.data
  const mayUpdate = can(user, 'billing.contract', 'update')
  const busy = activate.isPending || prolong.isPending || terminate.isPending
  const active = current.status === 'active'

  let primary: HeaderAction | null = null
  if (mayUpdate && current.status === 'draft') {
    primary = { id: 'activate', label: t('record.contract.activateContinue'), icon: 'sign', onClick: () => setDialog('activate'), disabled: busy }
  } else if (mayUpdate && active && current.validUntil) {
    primary = { id: 'prolong', label: t('record.contract.prolongContinue'), icon: 'arrow-right', onClick: () => setDialog('prolong'), disabled: busy }
  }
  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]
  if (mayUpdate && (active || current.status === 'draft')) {
    menu.push({ id: 'terminate', label: t('record.contract.terminateContinue'), icon: 'close', danger: true, onSelect: () => setDialog('terminate') })
  }

  const counterpartyName = labelOf(record.values, 'counterparty_id')
  const counterpartyHref = cardHref(user, 'sales.counterparty', current.counterpartyId)
  const term = current.validUntil
    ? `${current.validFrom ? formatDate(current.validFrom, locale) : '…'} — ${formatDate(current.validUntil, locale)}`
    : current.validFrom
      ? t('record.contract.openFrom', { date: formatDate(current.validFrom, locale) })
      : t('card.contract.openEnded')
  const meta = [
    <StatusBadge key="status" entity="billing.contract" value={current.status} />,
    counterpartyName ? (
      <span key="cp" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {counterpartyHref ? <Link to={counterpartyHref}>{counterpartyName}</Link> : counterpartyName}
      </span>
    ) : null,
    labelOf(record.values, 'organization_id') ? t('record.contract.organization', { org: labelOf(record.values, 'organization_id') ?? '' }) : null,
    <span key="currency">
      {t('record.contract.currency')} <span className="mono">{current.currency}</span>
    </span>,
    <span key="term">{term}</span>,
  ]

  // ------------------------------------------------------------------ этапы и факты

  const left = current.validUntil ? daysUntil(current.validUntil) : null
  const closed = current.status === 'expired' || current.status === 'terminated'
  const steps: StageStep[] = [
    { id: 'draft', label: t('value.billing.contract.status.draft'), state: current.status === 'draft' ? 'current' : 'done' },
    {
      id: 'active',
      label: t('value.billing.contract.status.active'),
      state: active ? 'current' : closed ? 'done' : 'future',
      meta: current.signedOn ? t('record.contract.since', { date: formatDate(current.signedOn, locale) }) : undefined,
    },
    {
      id: 'closed',
      label: closed ? t(`value.billing.contract.status.${current.status}`) : t('record.contract.stage.closed'),
      state: closed ? 'current' : 'future',
      meta: current.validUntil ? t('record.contract.until', { date: formatDate(current.validUntil, locale) }) : t('card.contract.openEnded'),
    },
  ]

  const facts: Fact[] = [
    {
      id: 'term',
      label: t('record.contract.fact.term'),
      value: current.validUntil ? t('record.contract.until', { date: formatDate(current.validUntil, locale) }) : t('card.contract.openEnded'),
      sub:
        left != null && active
          ? left < 0
            ? (
                <span className="flag flag-danger">
                  <Icon name="alert" />
                  {t('record.contract.expiredDays', { days: -left })}
                </span>
              )
            : t('record.contract.daysLeft', { days: left })
          : undefined,
    },
    { id: 'terms', label: t('card.contract.paymentTerms'), value: t('record.contract.days', { days: current.paymentTermsDays }) },
    {
      id: 'amount',
      label: t('card.contract.amount'),
      value: current.amount == null ? t('record.contract.noAmount') : formatMoney(current.amount, current.currency, locale),
    },
    {
      id: 'usable',
      label: t('card.contract.usableToday'),
      // Server's answer, with an icon and a word: colour alone carries no meaning.
      value: current.usableToday ? (
        <span className="flag flag-success">
          <Icon name="check" />
          {t('card.contract.usable')}
        </span>
      ) : (
        <span className="flag flag-danger">
          <Icon name="close" />
          {t('card.contract.notUsable')}
        </span>
      ),
      sub: textOf(record.values, 'contract_type') ? t(`value.billing.contract.contract_type.${textOf(record.values, 'contract_type')}`) : undefined,
    },
  ]

  const invoiceRows = invoices.data?.rows ?? []
  const documentRows = documents.data?.rows ?? []
  const docCount = (invoices.isSuccess ? invoiceRows.length : 0) + (documents.isSuccess ? documentRows.length : 0)

  const documentsTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      {mayReadInvoices && (
        <Panel title={t('nav.invoices')} count={invoices.isSuccess ? invoiceRows.length : null} flush>
          <RowsTable
            query={invoices}
            empty={t('record.contract.invoices.empty')}
            entity="billing.invoice"
            path={listPath('billing.invoice', '/invoices')}
            dateColumn="issued_on"
            extra={(row) => {
              const total = numberOf(row, 'total') ?? 0
              const paid = numberOf(row, 'paid_total') ?? 0
              return formatMoney(Math.max(0, total - paid), String(row.currency ?? current.currency), locale)
            }}
            extraLabel={t('record.contract.col.due')}
          />
        </Panel>
      )}
      {mayReadDocuments && (
        <Panel title={t('nav.documents')} count={documents.isSuccess ? documentRows.length : null} flush>
          <RowsTable
            query={documents}
            empty={t('record.contract.documents.empty')}
            entity="billing.document"
            path={listPath('billing.document', '/documents')}
            dateColumn="document_date"
          />
        </Panel>
      )}
      {!mayReadInvoices && !mayReadDocuments && <StateView compact kind="forbidden" title={t('record.contract.documents.forbidden')} />}
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.contracts'), to: listPath('billing.contract', '/contracts') }, { label: current.number, mono: true }]}
        title={t('record.contract.title', { number: current.number })}
        meta={meta}
        primary={primary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      <FailureAlert text={failure} />
      {notices}
      {current.status === 'terminated' && (
        <div className="alert alert-neutral" role="status">
          <Icon name="close" />
          <span>{t('record.contract.terminated')}</span>
        </div>
      )}
      <StageBar steps={steps} ariaLabel={t('record.contract.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'documents', label: t('record.contract.tab.documents'), count: docCount },
          { id: 'scan', label: t('card.contract.attachments.show') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.contract.sections')}
      >
        {tab === 'documents' && documentsTab}
        {tab === 'scan' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('card.contract.attachments.show')}>
              <AttachmentsPanel entityType={context.entity} entityId={recordId} resource={context.resource} />
            </Panel>
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>

      <FormModal
        open={dialog === 'activate'}
        icon="sign"
        overline={t('record.contract.activate.overline')}
        title={t('record.contract.title', { number: current.number })}
        confirmLabel={t('card.contract.action.activate')}
        canConfirm={signedOn !== ''}
        missingHint={t('record.contract.activate.missing')}
        pending={activate.isPending}
        failure={dialogFailure}
        onCancel={closeDialog}
        onConfirm={() => activate.mutate()}
      >
        <div className="form-grid">
          <DateField label={t('card.contract.signedOn')} value={signedOn} onChange={setSignedOn} required />
          <DateField
            label={t('card.contract.validUntil')}
            value={validUntil}
            onChange={setValidUntil}
            help={t('record.contract.activate.openHelp')}
          />
        </div>
      </FormModal>

      <FormModal
        open={dialog === 'prolong'}
        icon="arrow-right"
        overline={t('record.contract.prolong.overline')}
        title={t('record.contract.prolong.title', { number: current.number })}
        confirmLabel={t('card.contract.action.prolong')}
        canConfirm={prolongTo !== ''}
        missingHint={t('record.contract.prolong.missing')}
        pending={prolong.isPending}
        failure={dialogFailure}
        onCancel={closeDialog}
        onConfirm={() => prolong.mutate()}
      >
        <div className="form-grid">
          <div className="field">
            <span className="label">{t('record.contract.prolong.current')}</span>
            <div className="input is-readonly">{current.validUntil ? formatDate(current.validUntil, locale) : '—'}</div>
          </div>
          <DateField
            label={t('card.contract.prolongTo')}
            value={prolongTo}
            onChange={setProlongTo}
            required
            help={t('record.contract.prolong.help')}
          />
        </div>
      </FormModal>

      <FormModal
        open={dialog === 'terminate'}
        danger
        icon="close"
        title={t('record.contract.terminate.title', { number: current.number })}
        confirmLabel={t('card.contract.action.terminate')}
        canConfirm={terminatedOn !== '' && reason.trim() !== ''}
        missingHint={t('record.contract.terminate.missing')}
        pending={terminate.isPending}
        failure={dialogFailure}
        onCancel={closeDialog}
        onConfirm={() => terminate.mutate()}
      >
        <p className="secondary">{t('record.contract.terminate.text')}</p>
        <DateField label={t('record.contract.terminate.date')} value={terminatedOn} onChange={setTerminatedOn} required />
        <RequiredText label={t('card.contract.terminateReason')} value={reason} onChange={setReason} help={t('record.contract.terminate.help')} />
        <p className="strong" style={{ color: 'var(--fg)', margin: 0 }}>
          {t('record.contract.terminate.irreversible')}
        </p>
      </FormModal>
    </div>
  )
}

function RowsTable({
  query,
  empty,
  entity,
  path,
  dateColumn,
  extra,
  extraLabel,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; data: { rows: ListRow[] } | undefined }
  empty: string
  entity: string
  path: string
  dateColumn: string
  extra?: (row: ListRow) => string
  extraLabel?: string
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) {
    return (
      <StateView
        compact
        kind={query.error instanceof ApiError && query.error.isForbidden ? 'forbidden' : 'error'}
        title={t('record.contract.documents.error')}
      />
    )
  }
  const rows = query.data?.rows ?? []
  if (rows.length === 0) return <StateView compact kind="empty" title={empty} />
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="tbl">
        <thead>
          <tr>
            <th>{t('column.number')}</th>
            <th>{t('record.contract.col.date')}</th>
            <th className="num">{t('record.contract.col.total')}</th>
            {extra && <th className="num">{extraLabel}</th>}
            <th>{t('column.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <Link to={`${path}/${row.id}`} className="mono">
                  {String(row.number ?? row.id)}
                </Link>
              </td>
              <td>{typeof row[dateColumn] === 'string' ? formatDate(String(row[dateColumn]).slice(0, 10), locale) : '—'}</td>
              <td className="num">{formatMoney(numberOf(row, 'total'), typeof row.currency === 'string' ? row.currency : null, locale)}</td>
              {extra && <td className="num strong">{extra(row)}</td>}
              <td>
                <StatusBadge entity={entity} value={typeof row.status === 'string' ? row.status : null} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
